import {
  ChevronRight,
  Image,
  Info,
  List,
  PanelLeftClose,
  PanelRightClose,
  Search,
} from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, SearchInput, Skeleton, StatusMessage, Tooltip, ViewToggle } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { useDashboardData } from '../context';
import DashboardButton from './DashboardButton';
import WidgetLibraryDetailView from './WidgetLibraryDetailView';
import WidgetLibraryGridCard from './WidgetLibraryGridCard';
import WidgetLibraryListCard from './WidgetLibraryListCard';

/** List first (left), grid second (right) — matches legacy builder. */
/** Sidebar category order (matches legacy builder). */
const CATEGORY_SORT_WEIGHT = Object.freeze({
  Communication: 1,
  Scheduling: 2,
  Analytics: 3,
  Intelligence: 4,
});

const WIDGET_PREVIEW_SIZE_ORDER = ['small', 'medium', 'large'];

/**
 * A composition can only pin a `PUBLISHED` version — `create`/`update` reject
 * a draft widget with `DSW012 UI_WIDGET_QUERY_NOT_PUBLISHED`. `getAvailableWidgets`
 * still serves every widget regardless of status (so the catalog isn't empty
 * while publishing is upstream-limited); this is what stops a draft one from
 * being picked, without hiding it from the list.
 *
 * `widget.status` is already lowercased by `mapApiWidgetRowToUi` (the wire
 * vocabulary is `DRAFT`/`PUBLISHED`) — compare lowercase here too rather than
 * repeating that assumption.
 */
const isWidgetPublished = (widget) => widget?.status === 'published';

const WidgetLibraryModal = ({ open = false, onClose, onConfirm }) => {
  const { t } = useTranslation('dashboards');
  const { availableWidgets, loadingAvailableWidgets, fetchAvailableWidgets } = useDashboardData();
  const viewToggleOptions = useMemo(
    () => [
      { value: 'list', icon: List, label: t('widgetLibrary.listView') },
      { value: 'grid', icon: Image, label: t('widgetLibrary.gridView') },
    ],
    [t],
  );
  const [viewMode, setViewMode] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [selectedWidgetIds, setSelectedWidgetIds] = useState(new Set());
  const [expandedWidgetId, setExpandedWidgetId] = useState(null);
  const [previewSize, setPreviewSize] = useState('medium');

  useEffect(() => {
    if (open) fetchAvailableWidgets();
  }, [fetchAvailableWidgets, open]);

  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedWidgetIds(new Set());
    setSelectedCategory('all');
    setIsSidebarOpen(true);
    setExpandedWidgetId(null);
    setPreviewSize('medium');
  }, []);

  const resetState = useCallback(() => {
    clearFilters();
    onClose?.();
  }, [clearFilters, onClose]);

  const categories = useMemo(() => {
    const unique = [...new Set(availableWidgets.map((item) => item.category).filter(Boolean))];
    unique.sort(
      (a, b) =>
        (CATEGORY_SORT_WEIGHT[a] ?? 99) - (CATEGORY_SORT_WEIGHT[b] ?? 99) || a.localeCompare(b),
    );
    return ['all', ...unique];
  }, [availableWidgets]);

  const visibleWidgets = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return availableWidgets.filter((widget) => {
      const matchCategory = selectedCategory === 'all' || widget.category === selectedCategory;
      const matchSearch =
        !normalizedSearch ||
        widget.name?.toLowerCase().includes(normalizedSearch) ||
        widget.description?.toLowerCase().includes(normalizedSearch);
      return matchCategory && matchSearch;
    });
  }, [availableWidgets, searchTerm, selectedCategory]);

  const selectedWidgets = useMemo(
    () => availableWidgets.filter((widget) => selectedWidgetIds.has(widget.id)),
    [availableWidgets, selectedWidgetIds],
  );
  const expandedWidget = useMemo(
    () => availableWidgets.find((widget) => widget.id === expandedWidgetId) || null,
    [availableWidgets, expandedWidgetId],
  );
  const expandedWidgetIsSelected = expandedWidget ? selectedWidgetIds.has(expandedWidget.id) : false;
  const availablePreviewSizes = useMemo(() => {
    const sizes = expandedWidget?.sizes?.length ? expandedWidget.sizes : WIDGET_PREVIEW_SIZE_ORDER;
    return [...sizes].sort(
      (a, b) => WIDGET_PREVIEW_SIZE_ORDER.indexOf(a) - WIDGET_PREVIEW_SIZE_ORDER.indexOf(b),
    );
  }, [expandedWidget]);
  const selectedWidgetNames = selectedWidgets.map((item) => item.name).filter(Boolean).join(', ');
  const toggleSelection = useCallback((widgetId) => {
    setSelectedWidgetIds((prev) => {
      const next = new Set(prev);
      if (next.has(widgetId)) next.delete(widgetId);
      else next.add(widgetId);
      return next;
    });
  }, []);

  /** A draft widget never toggles — the backend would refuse it at save time anyway. */
  const handleCardToggle = useCallback(
    (widget) => () => {
      if (!isWidgetPublished(widget)) return;
      toggleSelection(widget.id);
    },
    [toggleSelection],
  );

  const handleCardKeyDown = useCallback(
    (widget) => (event) => {
      if ((event.key === 'Enter' || event.key === ' ') && isWidgetPublished(widget)) {
        event.preventDefault();
        toggleSelection(widget.id);
      }
    },
    [toggleSelection],
  );

  const handleActionButtonClick = useCallback(
    (widget) => (event) => {
      event.stopPropagation();
      if (!isWidgetPublished(widget)) return;
      toggleSelection(widget.id);
    },
    [toggleSelection],
  );
  const handleDetailActionClick = useCallback(
    (widget, isSelected) => () => {
      if (!isWidgetPublished(widget)) return;
      toggleSelection(widget.id);
      if (!isSelected) {
        setExpandedWidgetId(null);
      }
    },
    [toggleSelection],
  );

  const handleExpandPreviewClick = useCallback(
    (widget) => (event) => {
      event.stopPropagation();
      setExpandedWidgetId(widget.id);
      setPreviewSize(widget.defaultSize || widget.sizes?.[0] || 'medium');
    },
    [],
  );
  const handleBackFromDetail = useCallback(() => setExpandedWidgetId(null), []);

  /** List and grid share the same grid (up to 4 columns on xl); list cards omit the top preview. */
  const itemsContainerClass = 'grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4';

  return (
    <Modal
      isOpen={open}
      onClose={resetState}
      ariaLabel={t('widgetLibrary.title')}
      size="xl"
      portalRootId="shell-main-content"
      className="!max-w-[1440px] !h-[90vh] min-h-0 !gap-2 !pt-3 !pb-4"
    >
      {/* Single grid row: aside and main column (section + footer) share height so the aside border-r spans full height */}
      <div className="grid h-full min-h-0 flex-1 grid-cols-[auto_minmax(0,1fr)] gap-0">
          <aside
            className={`flex min-h-0 shrink-0 flex-col border-r border-edge transition-[width] duration-200 ${
              isSidebarOpen ? 'w-60' : 'w-12'
            }`}
          >
            <div
              className={`flex shrink-0 ${isSidebarOpen ? 'items-start justify-between px-3 pb-2 pt-2' : 'items-center justify-center py-2'}`}
            >
              {isSidebarOpen && (
                <p className="text-xs font-semibold uppercase tracking-wide text-content-muted">
                  {t('widgetLibrary.categoriesTitle')}
                </p>
              )}
              <DashboardButton
                variant="ghost"
                size="sm"
                className="!h-7 !w-7 !p-0"
                onClick={() => setIsSidebarOpen((prev) => !prev)}
                aria-label={
                  isSidebarOpen
                    ? t('widgetLibrary.collapseCategoriesAriaLabel')
                    : t('widgetLibrary.expandCategoriesAriaLabel')
                }
              >
                {isSidebarOpen ? <PanelLeftClose size={14} /> : <PanelRightClose size={14} />}
              </DashboardButton>
            </div>
            <div
              className={`scrollbar-aimsos-auto-sm flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-2 pb-2 ${
                isSidebarOpen ? 'flex' : 'hidden'
              }`}
            >
              {categories.map((category) => {
                const isActive = selectedCategory === category;
                return (
                <button
                  key={category}
                  type="button"
                  onClick={() => setSelectedCategory(category)}
                  className={`flex w-full items-center justify-between rounded-md border-0 border-l-[3px] py-2 pl-2 pr-3 text-left text-sm shadow-none outline-none ring-0 transition-colors focus-visible:ring-2 focus-visible:ring-brand-500/40 focus-visible:ring-offset-2 ${
                    isActive
                      ? 'border-l-brand-500 bg-brand-50 font-medium text-brand-500 dark:bg-brand-950/40 dark:text-brand-400'
                      : 'border-l-transparent text-content-secondary hover:bg-surface-hover-subtle'
                  }`}
                >
                  <span>{category === 'all' ? t('widgetLibrary.allCategory') : category}</span>
                  <ChevronRight
                    size={14}
                    className={
                      isActive
                        ? 'shrink-0 text-brand-500 dark:text-brand-400'
                        : 'shrink-0 text-content-muted opacity-70'
                    }
                  />
                </button>
                );
              })}
            </div>
          </aside>

          <div className="flex min-h-0 min-w-0 flex-col">
          <section className="flex min-h-0 flex-1 flex-col px-6 pt-1 pb-0">
            {expandedWidget ? (
              <WidgetLibraryDetailView
                widget={expandedWidget}
                previewSize={previewSize}
                previewSizes={availablePreviewSizes}
                onBack={handleBackFromDetail}
                onPreviewSizeChange={setPreviewSize}
              />
            ) : (
              <div className="flex min-h-0 flex-1 flex-col gap-4">
                <div className="space-y-2">
                  <h2 className="text-base font-bold leading-tight tracking-tight text-content">
                    {t('widgetLibrary.title')}
                  </h2>
                  <p className="text-sm text-content-muted">{t('widgetLibrary.description')}</p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <div className="max-w-[360px] flex-1">
                    <SearchInput
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      onClear={() => setSearchTerm('')}
                      placeholder={t('widgetLibrary.searchPlaceholder')}
                    />
                  </div>
                  <ViewToggle view={viewMode} onChange={setViewMode} options={viewToggleOptions} className="ml-auto" />
                </div>

                <div className="scrollbar-aimsos-auto min-h-0 flex-1 overflow-y-auto border-0 border-t-0 pr-1 shadow-none outline-none">
                  {loadingAvailableWidgets ? (
                <div className={itemsContainerClass}>
                  {Array.from({ length: 12 }).map((_, index) => (
                    <Skeleton
                      key={`widget-skeleton-${index}`}
                      height={viewMode === 'list' ? '120px' : '200px'}
                      width="100%"
                      borderRadius="12px"
                    />
                  ))}
                </div>
                  ) : visibleWidgets.length === 0 ? (
                <StatusMessage
                  icon={Search}
                  title={t('widgetLibrary.emptySearch')}
                  subtitle={t('widgetLibrary.emptySearchDescription')}
                />
                  ) : (
                <div className={itemsContainerClass}>
                  {visibleWidgets.map((widget) => {
                    const isSelected = selectedWidgetIds.has(widget.id);
                    const isGrid = viewMode === 'grid';
                    const disabled = !isWidgetPublished(widget);
                    return isGrid ? (
                      <WidgetLibraryGridCard
                        key={widget.id}
                        widget={widget}
                        isSelected={isSelected}
                        disabled={disabled}
                        disabledTooltip={t('widgetLibrary.detail.notPublishedTooltip')}
                        onCardToggle={handleCardToggle}
                        onCardKeyDown={handleCardKeyDown}
                        onActionButtonClick={handleActionButtonClick}
                        onExpandPreviewClick={handleExpandPreviewClick}
                        expandPreviewAriaLabel={t('widgetLibrary.expandPreviewAriaLabel')}
                      />
                    ) : (
                      <WidgetLibraryListCard
                        key={widget.id}
                        widget={widget}
                        isSelected={isSelected}
                        disabled={disabled}
                        disabledTooltip={t('widgetLibrary.detail.notPublishedTooltip')}
                        onCardToggle={handleCardToggle}
                        onCardKeyDown={handleCardKeyDown}
                        onActionButtonClick={handleActionButtonClick}
                      />
                    );
                  })}
                </div>
                  )}
                </div>
              </div>
            )}
          </section>

        <Modal.Footer className="mt-auto !border-0 !pt-2 !pb-0 flex-col items-stretch bg-surface">
          {!expandedWidget && selectedWidgets.length > 0 && (
            <div className="mb-3 flex min-h-[52px] w-full min-w-0 items-center gap-2 rounded-md border border-brand-100 bg-brand-50 px-4 py-2.5 dark:border-brand-900/60 dark:bg-brand-950/40">
              <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-surface text-brand-700 dark:bg-surface-elevated dark:text-brand-300">
                <Info size={14} />
              </span>
              {/*
                `flex-1 min-w-0` is what makes `truncate` below actually
                truncate: without it the paragraph's flex basis is its
                max-content width — 1129px with six widget names selected —
                and the whole modal grows to fit, pushing Cancel/Add off the
                right of the screen at any viewport (CT-5895).
              */}
              <div className="min-w-0 flex-1 leading-tight">
                <p className="text-sm font-semibold text-brand-700 dark:text-brand-300">
                  {t('widgetLibrary.selectedAlertTitle', { count: selectedWidgets.length })}
                </p>
                <p className="truncate text-sm text-brand-700/90 dark:text-brand-200/90">{selectedWidgetNames}</p>
              </div>
            </div>
          )}
          {expandedWidget ? (
            <div className="flex items-center justify-end gap-3">
              <Tooltip
                content={t('widgetLibrary.detail.notPublishedTooltip')}
                disabled={isWidgetPublished(expandedWidget) || expandedWidgetIsSelected}
                size="compact"
              >
                <DashboardButton
                  key={expandedWidget.id}
                  onClick={handleDetailActionClick(expandedWidget, expandedWidgetIsSelected)}
                  variant={expandedWidgetIsSelected ? 'secondary' : 'primary'}
                  disabled={!expandedWidgetIsSelected && !isWidgetPublished(expandedWidget)}
                >
                  {expandedWidgetIsSelected ? t('actions.remove') : t('actions.add')}
                </DashboardButton>
              </Tooltip>
            </div>
          ) : (
            <div className="flex items-center justify-end gap-3">
              <DashboardButton variant="secondary" onClick={resetState}>
                {t('actions.cancel')}
              </DashboardButton>
              <DashboardButton
                disabled={selectedWidgets.length === 0}
                onClick={() => {
                  onConfirm?.(selectedWidgets);
                  clearFilters();
                }}
              >
                {t('actions.add')}
              </DashboardButton>
            </div>
          )}
        </Modal.Footer>
          </div>
      </div>
    </Modal>
  );
};

export default memo(WidgetLibraryModal);
